/**
 * Shared tooltip content for Readiness Score and its components.
 * Used across athlete detail views, coach dashboards, and team assessments.
 */

/**
 * High-level tooltip explaining how the overall Readiness Score is calculated.
 */
export const READINESS_SCORE_TOOLTIP = {
  title: 'About Readiness Score',
  body: 'A comprehensive measure of training preparedness calculated from multiple physiological factors including heart rate variability (HRV), resting heart rate, sleep quality and duration, and training load balance. Each metric is compared against personal baselines to provide a personalized assessment of recovery status.',
};

// ---------------------------------------------------------------------------
// Injury Risk tooltips
// ---------------------------------------------------------------------------

export const INJURY_RISK_TOOLTIP = {
  title: 'About Injury Risk',
  body: 'A composite injury risk score calculated from wearable and subjective signals, each weighted by SHAP importance values from the CGM-ML model. Tiers: 0-5% Low, 5-15% Moderate, 15-35% Elevated, 35-70% High, 70-100% Critical.',
};

export const INJURY_RISK_CONFIDENCE_TOOLTIP = {
  title: 'Confidence',
  body: 'How much of the full signal set is available. More signals (wearable + subjective) increase confidence. Below 70% means key signals are missing and the score may underestimate true risk.',
};

export const INJURY_RISK_BASE_RISK_TOOLTIP = {
  title: 'Base Risk (Population)',
  body: 'The population baseline before individual signals are applied. This represents the average background injury risk for a typical athlete. Individual signal contributions (weighted by SHAP importance from the CGM-ML XGBoost model) then push the score above or below this baseline to produce the final risk score.',
};

export const INJURY_RISK_FACTORS_TOOLTIP = {
  title: 'Top Risk Factors',
  body: 'Signals currently increasing injury risk. The bar shows each signal\'s weighted contribution to the overall score (SHAP weight x risk level). The percentage is the signal\'s individual risk level based on the athlete\'s current value vs. evidence-based thresholds.',
};

export const INJURY_RISK_AMPLIFIERS_TOOLTIP = {
  title: 'Risk Amplifiers',
  body: 'Multiplicative penalties applied when dangerous signal combinations occur simultaneously (e.g., high soreness + load spike). These compound effects increase the overall risk score beyond the sum of individual signals.',
};

/** Per-signal tooltip definitions for injury risk factors. */
export const INJURY_SIGNAL_TOOLTIPS: Record<string, { title: string; body: string }> = {
  soreness_3d_max: { title: '3-Day Max Soreness', body: 'Highest soreness reported over the past 3 days. The #1 predictor in the model. Sigmoid threshold centered at 6.5/10.' },
  soreness: { title: 'Soreness', body: 'Current single-day soreness rating (1-10). Higher values indicate tissue stress. Sigmoid threshold at 6.0/10.' },
  mental_fatigue: { title: 'Mental Fatigue', body: 'Self-reported mental fatigue (1-10). Elevated cognitive fatigue is linked to impaired neuromuscular control. Sigmoid threshold at 6.5/10.' },
  rpe: { title: 'RPE (Rating of Perceived Exertion)', body: 'How hard the athlete perceived the last session (1-10). Values above 7.5 indicate high internal load relative to capacity.' },
  motivation: { title: 'Motivation', body: 'Self-reported motivation (1-10). Inverted: low motivation increases risk. A potential early sign of overtraining (Meeusen 2013).' },
  sleep_hours: { title: 'Sleep Duration', body: 'Hours of sleep. Less than 6 hours significantly increases injury risk. Below 5 hours is critical (Milewski 2014).' },
  sleep_score: { title: 'Sleep Quality', body: 'Device-reported sleep quality score (0-100). Inverted: lower quality = higher risk. Reflects sleep architecture and restfulness.' },
  rhr: { title: 'Resting Heart Rate', body: 'RHR compared to personal baseline. Elevation above baseline may indicate overtraining, illness, or insufficient recovery (Meeusen 2013).' },
  hrv_rmssd: { title: 'HRV (RMSSD)', body: 'Heart rate variability compared to baseline. Drop below baseline = impaired parasympathetic recovery. Lower HRV = higher risk.' },
  hrv_7d_cv: { title: 'HRV Variability', body: 'Coefficient of variation of HRV over 7 days. Threshold: 7% for elite, 10% for recreational athletes (Plews 2013). Instability suggests autonomic dysregulation.' },
  hrv_slope: { title: 'HRV Trend', body: 'Direction HRV is trending over recent days. A declining trend indicates worsening recovery capacity.' },
  rhr_slope: { title: 'RHR Trend', body: 'Direction resting heart rate is trending. A rising trend may signal developing overtraining or illness.' },
  resp_rate: { title: 'Respiratory Rate', body: 'Breathing rate compared to personal baseline. Deviations may indicate illness, stress, or autonomic imbalance.' },
  skin_temp: { title: 'Skin Temperature', body: 'Skin temperature deviation from baseline. Elevation may indicate inflammation, infection, or illness onset.' },
  acwr_composite: { title: 'Training Load Ratio (ACWR)', body: 'Acute (7-day) load divided by chronic (28-day) load. Sweet spot: 0.8-1.3. Above 1.5 = danger zone (Gabbett 2016).' },
  session_load_spike_ratio: { title: 'Session Load Spike', body: 'How much the latest session exceeded the 30-day maximum. Above 110% = 64% increased injury risk (Nielsen 2025).' },
  load_change_pct_7d: { title: 'Weekly Load Change', body: 'Percentage change in training load week-over-week. Increases above 30% raise injury hazard ratio to 1.59 (Malisoux 2015).' },
  load_7d_sum: { title: '7-Day Load', body: 'Cumulative training load over the past 7 days relative to the athlete\'s typical weekly load.' },
  consecutive_high_soreness: { title: 'Persistent Soreness', body: 'Number of consecutive days with soreness above 6/10. Indicates tissue recovery is not keeping pace with training demands.' },
  rpe_load_divergence: { title: 'RPE-Load Mismatch', body: 'Divergence between perceived exertion and objective training load. Positive values = the athlete feels the session was harder than the load suggests.' },
  load_volatility: { title: 'Load Volatility', body: '7-day rolling standard deviation of training load. Erratic, unpredictable loading patterns increase injury risk.' },
  wearable_missing_rate_7d: { title: 'Data Completeness', body: 'Percentage of expected wearable data points missing over 7 days. High missingness reduces assessment accuracy.' },
};

/**
 * Plain-English tooltip content for each readiness component.
 * Explains how each metric is calculated and what optimal values look like.
 */
export const COMPONENT_TOOLTIPS: Record<string, { title: string; body: string }> = {
  HRV: {
    title: 'HRV (Heart Rate Variability)',
    body: 'Compared to your last 14 days. Above your average scores higher; below or jumpy HRV scores lower. Optimal: at or above your personal baseline.',
  },
  RHR: {
    title: 'Resting Heart Rate',
    body: 'Compared to your last 14 days. Lower than baseline adds points; higher subtracts (~5 per bpm). Optimal: at or below your personal baseline.',
  },
  Sleep: {
    title: 'Sleep',
    body: 'Mostly how last night compares to your usual (14 days); a small part is duration. Optimal: at or above your usual. Personal baseline only.',
  },
  ACWR: {
    title: 'ACWR (Acute:Chronic Workload Ratio)',
    body: 'Only component not personal-baseline. Acute load (7 days) ÷ chronic (28 days). Optimal: 0.80–1.30 (sweet spot 1.0–1.1). Above 1.30 = higher injury risk.',
  },
  Load: {
    title: 'Load (ACWR or Strain)',
    body: 'Training load balance. ACWR (Garmin): Optimal 0.80–1.30. Strain (WHOOP): Optimal 8–14. Higher values indicate increased fatigue or injury risk.',
  },
  Wellness: {
    title: 'Wellness',
    body: 'Mood, stress, soreness combined. Optimal when reported wellness is high. Personal patterns when available.',
  },
};
